// src/services/push/index.js
//
// The browser's push entry point. The rest of the CRM imports THIS, never a
// provider. Selection is by environment (REACT_APP_PUSH_PROVIDER), so changing
// provider is a config change here exactly as it is on the backend.

import FirebaseWebPushProvider from './FirebaseWebPushProvider';
import WebPushProvider from './WebPushProvider';
import API from '../api';

/** name → provider class. Add a provider by adding one line. */
const PROVIDERS = {
  [FirebaseWebPushProvider.providerName]: FirebaseWebPushProvider,
};

/** Delivers nothing, quietly. Used when push is not configured. */
class NoopWebPushProvider extends WebPushProvider {
  static providerName = 'noop';

  isConfigured() { return true; }

  isSupported() { return false; }
}

const PROVIDER_NAME = (process.env.REACT_APP_PUSH_PROVIDER || 'fcm').toLowerCase();

class PushService {
  constructor() {
    const Klass = PROVIDERS[PROVIDER_NAME];
    const candidate = Klass ? new Klass() : null;
    this.provider = candidate && candidate.isConfigured() && candidate.isSupported()
      ? candidate
      : new NoopWebPushProvider();
    this.token = null;
  }

  get providerName() { return this.provider.name; }

  /** Is push actually usable in this browser, with this configuration? */
  get available() { return this.provider.isSupported() && this.provider.isConfigured(); }

  /**
   * Obtain a token and register it with the CRM backend.
   * Safe to call on every login — the backend upserts by token.
   */
  async register() {
    if (!this.available) return null;
    try {
      const token = await this.provider.requestToken();
      if (!token) return null;
      this.token = token;
      await API.post('/push/register', {
        token,
        platform: 'WEB',
        deviceName: navigator.userAgent?.slice(0, 120),
      });
      return token;
    } catch (err) {
      // Push is a convenience; never let it break sign-in.
      console.warn('[push] registration failed:', err?.message || err);
      return null;
    }
  }

  /** Stop this browser receiving push (called on logout). */
  async unregister() {
    const token = this.token;
    this.token = null;
    try {
      if (token) await API.post('/push/unregister', { token });
    } catch (err) { /* the session may already be gone; nothing to do */ }
    try { await this.provider.deleteToken(); } catch (err) { /* best effort */ }
  }

  /** Messages that arrive while a CRM tab is focused. */
  onForegroundMessage(cb) {
    if (!this.available) return () => {};
    return this.provider.onMessage(cb);
  }
}

const pushService = new PushService();
export default pushService;
